import React from 'react'
import { Button as ChakraButton, ButtonProps } from '@chakra-ui/core'
import { X } from 'lucide-react'
import { isRtlSelector } from 'redux/selectors/general'
import { useSelector } from 'react-redux'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon' | 'filter' | 'tab'

export interface IButtonProps extends Omit<ButtonProps, 'variant'> {
  // Associate footer actions with their form without nesting the modal shell in it.
  form?: string
  // Anchor attributes are forwarded when rendering as="a".
  href?: string
  target?: string
  rel?: string
  download?: string | boolean
  variant?: ButtonVariant
  // For filter variant: whether the filter is currently applied
  isActive?: boolean
  // For filter variant: the active fill color (usually theme.elementsColor)
  activeColor?: string
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
}

const TRANSITION = 'all 0.15s ease-out'

export const getButtonVariantStyles = (
  variant: ButtonVariant,
  isActive?: boolean,
  activeColor?: string
): Partial<ButtonProps> => {
  switch (variant) {
    case 'primary':
      return {
        bg: '#0F172A',
        color: 'white',
        border: 'none',
        boxShadow: '0 1px 2px rgba(15,23,42,0.12)',
        _hover: {
          bg: '#1E293B',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 14px rgba(15,23,42,0.22)',
        },
        _active: { bg: '#020617', transform: 'scale(0.97)', boxShadow: 'none' },
        _focus: { boxShadow: '0 0 0 3px rgba(15,23,42,0.18)', outline: 'none' },
        _disabled: { opacity: 0.45, cursor: 'not-allowed', transform: 'none' },
      }

    case 'secondary':
      return {
        bg: 'white',
        color: '#0F172A',
        borderWidth: '1.5px',
        borderStyle: 'solid',
        borderColor: '#E2E8F0',
        _hover: {
          borderColor: '#94A3B8',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
        },
        _active: { transform: 'scale(0.97)', boxShadow: 'none' },
        _focus: { boxShadow: '0 0 0 3px rgba(15,23,42,0.10)', outline: 'none', borderColor: '#0F172A' },
        _disabled: { opacity: 0.45, cursor: 'not-allowed', transform: 'none' },
      }

    case 'ghost':
      return activeColor
        ? {
            bg: 'transparent',
            color: isActive ? activeColor : '#94A3B8',
            border: 'none',
            _hover: { bg: 'transparent', color: activeColor },
            _active: { bg: 'transparent' },
            _focus: { boxShadow: 'none', outline: 'none' },
            _disabled: { opacity: 0.45, cursor: 'not-allowed' },
          }
        : {
            bg: 'transparent',
            color: '#334155',
            border: 'none',
            _hover: { bg: '#F1F5F9' },
            _active: { bg: '#E2E8F0' },
            _focus: { boxShadow: 'none', outline: 'none' },
            _disabled: { opacity: 0.45, cursor: 'not-allowed' },
          }

    case 'danger':
      return {
        bg: '#DC2626',
        color: 'white',
        border: 'none',
        boxShadow: '0 1px 2px rgba(220,38,38,0.15)',
        _hover: {
          bg: '#B91C1C',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 14px rgba(220,38,38,0.28)',
        },
        _active: { bg: '#991B1B', transform: 'scale(0.97)', boxShadow: 'none' },
        _focus: { boxShadow: '0 0 0 3px rgba(220,38,38,0.22)', outline: 'none' },
        _disabled: { opacity: 0.45, cursor: 'not-allowed', transform: 'none' },
      }

    case 'icon':
      return {
        bg: 'transparent',
        color: '#64748B',
        border: 'none',
        p: '7px',
        minW: '34px',
        h: '34px',
        _hover: { bg: '#F1F5F9', color: '#0F172A' },
        _active: { bg: '#E2E8F0', transform: 'scale(0.88)' },
        _focus: { boxShadow: '0 0 0 3px rgba(15,23,42,0.08)', outline: 'none' },
        _disabled: { opacity: 0.45, cursor: 'not-allowed' },
      }

    case 'tab':
      return {
        bg: 'transparent',
        color: isActive ? activeColor ?? '#0F172A' : '#94A3B8',
        border: 'none',
        borderRadius: '0',
        _hover: { bg: 'transparent', color: activeColor ?? '#0F172A' },
        _active: { bg: 'transparent' },
        _focus: { boxShadow: 'none', outline: 'none' },
        _disabled: { opacity: 0.45, cursor: 'not-allowed' },
      }

    case 'filter':
      return {
        bg: isActive ? activeColor ?? '#0F172A' : 'white',
        color: isActive ? 'white' : '#334155',
        borderWidth: '1.5px',
        borderStyle: 'solid',
        borderColor: isActive ? 'transparent' : '#E2E8F0',
        _hover: {
          borderColor: isActive ? 'transparent' : '#94A3B8',
          boxShadow: '0 1px 6px rgba(15,23,42,0.08)',
        },
        _active: { transform: 'scale(0.97)' },
        _focus: { boxShadow: '0 0 0 3px rgba(15,23,42,0.10)', outline: 'none' },
        _disabled: { opacity: 0.45, cursor: 'not-allowed' },
      }
  }
}

export const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  ({ variant = 'primary', isActive, activeColor, startIcon, endIcon, children, ...rest }, ref) => {
    const variantStyles = getButtonVariantStyles(variant, isActive, activeColor)
    const isRtl = useSelector(isRtlSelector)
    return (
      <ChakraButton
        ref={ref}
        borderRadius="8px"
        fontWeight="500"
        fontSize="14px"
        cursor="pointer"
        transition={TRANSITION}
        display="inline-flex"
        alignItems="center"
        {...variantStyles}
        {...rest}
      >
        {startIcon && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: isRtl ? 0 : children ? '6px' : 0,
              marginLeft: isRtl ? (children ? '6px' : 0) : 0,
              flexShrink: 0,
            }}
          >
            {startIcon}
          </span>
        )}
        {children}
        {endIcon && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              // Mirror of startIcon: the gap belongs between icon and label, whichever side that is
              marginLeft: isRtl ? 0 : children ? '6px' : 0,
              marginRight: isRtl ? (children ? '6px' : 0) : 0,
              flexShrink: 0,
            }}
          >
            {endIcon}
          </span>
        )}
      </ChakraButton>
    )
  }
)

Button.displayName = 'Button'

// CloseButton — icon variant pre-wired with X icon; used in modals, drawers, tooltips. Its body is always the
// X glyph, so `children` is omitted from the prop type (a passed-in value would be silently ignored anyway).
export const CloseButton = React.forwardRef<
  HTMLButtonElement,
  Omit<IButtonProps, 'variant' | 'startIcon' | 'endIcon' | 'children'>
>((props, ref) => (
  <Button ref={ref} variant="icon" aria-label="close" {...props}>
    <X size={16} />
  </Button>
))

CloseButton.displayName = 'CloseButton'
